import {
  Desktop,
  devices,
  TabletOrMobile,
} from "@/utils/breakpoints"
import React, {
  RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react"
import styled, { css } from "styled-components"

const MobileColumn = styled.div`
  display: flex;
  flex-direction: column;

  align-items: stretch;
`

const Container = styled.div`
  position: relative;

  display: flex;
  flex-direction: row;

  max-width: var(--w-screen);
  overflow-x: scroll;

  -ms-overflow-style: none;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  @media ${devices.desktop} {
    flex-direction: column;
  }
`

const Tab = styled.div<{ active: boolean }>`
  font-weight: 500;
  transition: color 200ms ease-in-out,
    background-color 300ms ease-in-out;

  align-self: stretch;
  flex-shrink: 0;

  cursor: pointer;

  ${(props) =>
    props.active
      ? css`
          color: var(--color-primary-500) !important;
        `
      : css`
          color: var(--color-blue-900);
        `};

  @media ${devices.desktop} {
    & + & {
      border-left: unset;
      border-top: 1px solid var(--color-separator);
    }

    :hover {
      background-color: var(--color-blue-200);
    }

    text-align: left;
  }
`

const IndicatorContainer = styled.div``

const IndicatorBackground = styled.div`
  background-color: var(--color-separator);
  margin: -2px 0 0 0;
  height: 2px;

  flex-basis: 0;
  flex-grow: 1;
  overflow-y: auto;

  border-radius: 4px;
`

const IndicatorRow = styled.div<{
  left: number
  width: number
}>`
  position: absolute;
  z-index: 6;
  left: ${(props) => props.left}px;
  width: ${(props) => props.width}px;
  bottom: 0;

  height: 2px;

  background-color: var(--color-primary-500);
  border-radius: 2px;

  transition: left 300ms ease-in-out,
    width 200ms ease-in-out;
`

const IndicatorColumn = styled.div<{
  top: number
  height: number
}>`
  position: absolute;

  top: ${(props) => props.top}px;
  height: ${(props) => props.height}px;
  left: 0;
  width: 2px;

  background-color: var(--color-primary-500);
  border-radius: 0 2px 2px 0;

  transition: top 300ms ease-in-out,
    height 200ms ease-in-out;
`

type MaybeDiv = HTMLElement | null

interface IndicatorProps {
  refs: RefObject<MaybeDiv[]>
  parent: RefObject<HTMLDivElement>
  active: number
}

const Indicator = (props: IndicatorProps) => {
  const { refs, active, parent } = props

  const ref = refs.current && refs.current[active]
  const parentDiv = parent.current
  if (!ref || !parentDiv) return <></>

  const rect = ref.getBoundingClientRect()
  const parentRect = parentDiv.getBoundingClientRect()

  const margin = 4
  const top = rect.y - parentRect.y + margin
  const height = rect.height - 2 * margin

  const left = ref.offsetLeft
  const width = rect.width

  return (
    <IndicatorContainer>
      <Desktop>
        <IndicatorColumn top={top} height={height} />
      </Desktop>
      <TabletOrMobile>
        <IndicatorRow left={left} width={width} />
      </TabletOrMobile>
    </IndicatorContainer>
  )
}

interface TabsProps {
  selected: number
  tabs: React.ReactNode[]
  refs: HTMLElement[]
  className?: string
}

const Tabs = (props: TabsProps) => {
  const { selected, tabs, refs, className } = props
  const [active, setActive] = useState(0)
  const tabRefs = useRef<MaybeDiv[]>([])
  const container = useRef<HTMLDivElement>(null)

  const getActiveTabIndex = (
    windowScroll: number,
    elementScroll: number[],
  ) => {
    if (elementScroll[0] > windowScroll) return 0

    for (const i in elementScroll)
      if (elementScroll[i] > windowScroll)
        return Number(i) - 1

    return elementScroll.length - 1
  }

  const checkCurrent = useCallback(() => {
    const currentActive = getActiveTabIndex(
      window.scrollY,
      refs.map((ref) => ref.offsetTop),
    )
    const isNotDesktop = !window.matchMedia(devices.desktop)
      .matches

    if (isNotDesktop) {
      const currentTab = tabRefs.current[currentActive]
      currentTab?.scrollIntoView({
        inline: "center",
      })
    }

    if (active !== currentActive) {
      setActive(currentActive)
    }
  }, [active, setActive, refs])

  useEffect(() => {
    setActive(selected)
    tabRefs.current = tabRefs.current.slice(0, tabs.length)
  }, [selected, tabs.length])

  useEffect(() => {
    checkCurrent()
    addEventListener("scroll", checkCurrent)
    return () => removeEventListener("scroll", checkCurrent)
  }, [checkCurrent])

  return (
    <MobileColumn>
      <Container ref={container} className={className}>
        {tabs.map((tab, index) => (
          <Tab
            active={active === index}
            ref={(node) =>
              tabRefs.current &&
              (tabRefs.current[index] = node)
            }
            onClick={() =>
              window.scrollTo({
                top: refs[index].offsetTop + 5,
                behavior: "smooth",
              })
            }
            key={index}>
            {tab}
          </Tab>
        ))}
        <Indicator
          refs={tabRefs}
          active={active}
          parent={container}
        />
      </Container>
      <TabletOrMobile>
        <IndicatorBackground />
      </TabletOrMobile>
    </MobileColumn>
  )
}

export default Tabs
